
import { createContext, useContext, useState, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { pageVariants } from '@/lib/animations';

interface AnimationContextType {
  exitBeforeEnter: boolean;
  isAnimationEnabled: boolean;
  pageTransition: {
    initial: any;
    animate: any;
    exit: any;
    transition: any;
  };
}

const AnimationContext = createContext<AnimationContextType>({
  exitBeforeEnter: true,
  isAnimationEnabled: true,
  pageTransition: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  }
});

export const useAnimationContext = () => useContext(AnimationContext);
export const useAnimation = () => useContext(AnimationContext);

interface AnimationProviderProps {
  children: ReactNode;
}

export const AnimationProvider = ({ children }: AnimationProviderProps) => {
  const [exitBeforeEnter] = useState(true);
  const [isAnimationEnabled] = useState(true);

  const pageTransition = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  };

  return (
    <AnimationContext.Provider value={{ 
      exitBeforeEnter, 
      isAnimationEnabled, 
      pageTransition 
    }}>
      {children}
    </AnimationContext.Provider>
  );
};

interface AnimatedRouteProps {
  children: ReactNode;
  path?: string;
}

export const AnimatedPage = ({ children }: AnimatedRouteProps) => {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      className="w-full"
    >
      {children}
    </motion.div>
  );
};

export const PageTransition = ({ children }: { children: ReactNode }) => {
  const { exitBeforeEnter } = useAnimationContext();
  
  return (
    <AnimatePresence mode={exitBeforeEnter ? "wait" : "sync"}>
      {children}
    </AnimatePresence>
  );
};
